/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FixedActionBar.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Fixed action bar component for persistent action buttons
    at the bottom of forms and pages with consistent styling.

 🔧 Notes:
    - Implements fixed positioning at bottom of viewport
    - Supports primary and secondary action buttons
    - Integrates with Tailwind CSS for consistent styling
    - Provides accessibility features and keyboard navigation
    - Supports custom className and children props

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { Button } from './button';
import { Save, X, Trash2, Loader2, RotateCcw } from 'lucide-react';
import { useSidebarState } from '../../hooks/useSidebarState';
import { SaveButtonWithDropdown, type SaveAction } from './SaveButtonWithDropdown';

interface AdditionalButton {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary';
  disabled?: boolean;
}

import SimpleTooltip from './SimpleTooltip';

interface FixedActionBarProps {
  onSave?: (e?: React.FormEvent) => void | Promise<void>;
  onSaveWithAction?: (action: SaveAction) => void | Promise<void>; // New: callback with save action
  onCancel?: () => void;
  onDelete?: () => void;
  onReset?: () => void;
  onSaveAndNew?: () => void | Promise<void>; // Callback for "Save and new" action
  saveText?: string | React.ReactNode;
  cancelText?: string;
  deleteText?: string;
  resetText?: string | React.ReactNode;
  saveTooltip?: string;
  cancelTooltip?: string;
  deleteTooltip?: string;
  saveDisabled?: boolean;
  deleteDisabled?: boolean;
  resetDisabled?: boolean;
  isLoading?: boolean;
  isSaving?: boolean;  // Alias for isLoading
  isSubmitting?: boolean;  // Alias for isLoading/isSaving
  showSave?: boolean;
  showCancel?: boolean;
  showDelete?: boolean;  // Control Delete button visibility
  isEditMode?: boolean;  // If true, Delete button is shown (unless showDelete is explicitly false)
  hasChanges?: boolean;  // Track if form has unsaved changes
  useSaveDropdown?: boolean; // Enable save button with dropdown
  saveDropdownStorageKey?: string; // localStorage key for save action preference
  availableSaveActions?: SaveAction[]; // Optional: filter available save actions (e.g., ['save', 'save-and-close'])
  additionalButtons?: AdditionalButton[];
  className?: string;
  children?: React.ReactNode;
  title?: React.ReactNode;  // New: title/icon section on the left
}

export const FixedActionBar: React.FC<FixedActionBarProps> = ({
  onSave,
  onSaveWithAction,
  onCancel,
  onDelete,
  onReset,
  onSaveAndNew,
  saveText = 'Save',
  cancelText = 'Cancel',
  deleteText = 'Delete',
  resetText = 'Reset',
  saveTooltip,
  cancelTooltip,
  deleteTooltip,
  saveDisabled = false,
  deleteDisabled = false,
  resetDisabled = false,
  isLoading = false,
  isSaving,
  showSave = true,
  showCancel = true,
  showDelete,  // undefined means auto-determine from isEditMode
  isEditMode = false,  // Default to false (create mode)
  useSaveDropdown = false,
  saveDropdownStorageKey,
  availableSaveActions,
  additionalButtons = [],
  className = '',
  children,
  title
}) => {
  // Standard: Delete button only shows in edit mode (unless explicitly controlled)
  const shouldShowDelete = showDelete !== undefined ? showDelete : (isEditMode && !!onDelete);
  const { isOpen: isSidebarOpen, width: sidebarWidth } = useSidebarState();

  // Use isSaving if provided, otherwise use isLoading
  const loading = isSaving !== undefined ? isSaving : isLoading;

  // Use a default width if sidebarWidth is 0 to prevent flickering
  const effectiveSidebarWidth = sidebarWidth === 0 ? 350 : sidebarWidth;

  return (
    <div
      className={`fixed bottom-0 z-50 fixed-action-bar right-0 bg-card border-t border-border ${className}`}
      style={{
        left: `${effectiveSidebarWidth + 1}px` // +1px para evitar solapamiento
      }}
    >
      <div className="h-16 px-6">
        <div className="flex items-center h-full">
          {/* Left side - Title, Delete, Reset buttons and custom content */}
          <div className="flex items-center space-x-3">
            {/* Title section */}
            {title && (
              <div className="flex items-center gap-2 mr-4">
                {title}
              </div>
            )}
            {/* Standard: Delete button only shows in edit mode */}
            {shouldShowDelete && onDelete && (
              <SimpleTooltip content={deleteTooltip || ''}>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={onDelete}
                  disabled={deleteDisabled || loading}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleteText}
                </Button>
              </SimpleTooltip>
            )}
            {onReset && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onReset}
                disabled={resetDisabled || loading}
                className="flex items-center gap-2"
              >
                {typeof resetText === 'string' ? (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    {resetText}
                  </>
                ) : (
                  resetText
                )}
              </Button>
            )}
          </div>

          {/* Center - Custom content (e.g., progress bars) */}
          {children && (
            <div className="flex-1 flex items-center min-w-0 px-4">
              {children}
            </div>
          )}

          {/* Right side - Additional, Save and Cancel buttons */}
          <div className="flex items-center space-x-3 ml-auto">
            {/* Additional custom buttons */}
            {additionalButtons.map((btn) => (
              <Button
                key={btn.key}
                type="button"
                variant={btn.variant || 'outline'}
                size="sm"
                onClick={btn.onClick}
                disabled={btn.disabled || loading}
                className="flex items-center gap-2"
              >
                {btn.icon}
                {btn.label}
              </Button>
            ))}

            {onCancel && showCancel && (
              <SimpleTooltip content={cancelTooltip || ''}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onCancel}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  {cancelText}
                </Button>
              </SimpleTooltip>
            )}
            {onSave && showSave && !useSaveDropdown && (
              <SimpleTooltip content={saveTooltip || ''}>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={onSave}
                  disabled={saveDisabled || loading}
                  className="flex items-center gap-2 min-w-[100px]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : typeof saveText === 'string' ? (
                    <>
                      <Save className="h-4 w-4" />
                      {saveText}
                    </>
                  ) : (
                    saveText
                  )}
                </Button>
              </SimpleTooltip>
            )}
            {useSaveDropdown && (onSaveWithAction || onSave) && showSave && (
              <SimpleTooltip content={saveTooltip || ''}>
                <div>
                  <SaveButtonWithDropdown
                    onSave={async (action) => {
                      if (onSaveWithAction) {
                        await onSaveWithAction(action);
                      } else if (onSave) {
                        // Fallback: if onSaveWithAction is not provided, use onSave
                        // This maintains backward compatibility
                        await onSave();
                      }
                    }}
                    disabled={saveDisabled}
                    isLoading={loading}
                    storageKey={saveDropdownStorageKey}
                    availableActions={availableSaveActions}
                  />
                </div>
              </SimpleTooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FixedActionBar;
